/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { objectSpread2 as _objectSpread2, toConsumableArray as _toConsumableArray } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import React from 'react';
import { getPanelDetails, getPanelId } from '../utils/getPanelDetails.js';
import callIf from '../utils/callIf.js';

/**
 * Builds breadcrumbs for a nested panel by iterating over a set of panels and adds a panel
 * to breadcrumbs if it comes before the current panel.
 */
var buildBreadCrumbs = function buildBreadCrumbs(panels, panelIndex, parentBreadcrumbText, parentPanelId) {
  return panels.reduce(function (arr, panel, breadCrumbIndex) {
    var _panel$props;
    var value = panel === null || panel === void 0 || (_panel$props = panel.props) === null || _panel$props === void 0 ? void 0 : _panel$props.breadCrumbText;
    var breadcrumbId = getPanelId(panel, breadCrumbIndex, true);
    return breadCrumbIndex > panelIndex && value && breadcrumbId ? arr : [].concat(_toConsumableArray(arr), [{
      value: value,
      id: breadcrumbId
    }]);
  },
  // Set the initial breadcrumb to the parent panel.
  [{
    value: parentBreadcrumbText,
    id: parentPanelId
  }]);
};

/**
 * Nested panels require that the onDoneClick function be overridden, as clicking done
 * should trigger opening the previous panel.
 */
var nestedPanelOnDoneClick = function nestedPanelOnDoneClick(nestedPanel, setActivePanelById, previousNestedId, parentPanelId, event) {
  if (nestedPanel.props.onDoneClick) {
    callIf(nestedPanel.props.onDoneClick, function () {
      setActivePanelById(previousNestedId || parentPanelId);
    })(event);
  }
  return false;
};
var SidePanelNestedPanels = function SidePanelNestedPanels(_ref) {
  var activePanelId = _ref.activePanelId,
    isOpen = _ref.isOpen,
    parentBreadcrumbText = _ref.parentBreadcrumbText,
    panelSize = _ref.panelSize,
    closePanel = _ref.closePanel,
    childCloseRef = _ref.childCloseRef,
    containerPanelProps = _ref.containerPanelProps,
    nestedPanels = _ref.nestedPanels,
    onPageSelect = _ref.onPageSelect,
    hasOverlay = _ref.hasOverlay,
    setActivePanelById = _ref.setActivePanelById,
    parentPanelId = _ref.parentPanelId;
  return nestedPanels.map(function (nestedPanel, index, panels) {
    var _getPanelDetails = getPanelDetails(nestedPanel, index, activePanelId, panels, true),
      nestedId = _getPanelDetails.id,
      isNextActive = _getPanelDetails.isNextActive,
      previousNestedId = _getPanelDetails.previousId;
    var nestedBreadCrumbs = buildBreadCrumbs(panels, index, parentBreadcrumbText, parentPanelId);
    var onPreviousClick = nestedPanel.props.onPreviousClick || containerPanelProps.onPreviousClick;
    return /*#__PURE__*/React.cloneElement(nestedPanel, _objectSpread2(_objectSpread2(_objectSpread2({}, containerPanelProps), nestedPanel.props), {}, {
      id: nestedId,
      key: nestedId,
      onDoneClick: function onDoneClick(event) {
        return nestedPanelOnDoneClick(nestedPanel, setActivePanelById, previousNestedId, parentPanelId, event);
      },
      onPreviousClick: onPreviousClick,
      onCloseClick: function onCloseClick(event, fromOverlay) {
        // When clicking on the Close button of a nested panel we want to return to the
        // parent panel. However, when clicking on the overlay we want to close everything
        // as would happen normally.
        if (fromOverlay) {
          if (containerPanelProps.onCloseClick) {
            containerPanelProps.onCloseClick(event);
          }
        } else {
          if (nestedPanel.props.onCloseClick) {
            nestedPanel.props.onCloseClick(event);
          }
          setActivePanelById(parentPanelId);
        }
      },
      internal: {
        isOpen: (isNextActive || nestedId === activePanelId) && isOpen,
        breadcrumbs: _toConsumableArray(nestedBreadCrumbs),
        containerPanelProps: containerPanelProps,
        activePanelId: activePanelId,
        panelSize: panelSize,
        setActivePanelById: setActivePanelById,
        closePanel: closePanel,
        childCloseRef: childCloseRef,
        onPageSelect: onPageSelect,
        nextId: null,
        hasOverlay: hasOverlay,
        renderPrimary: !!nestedPanel.props.onDoneClick,
        previousId: previousNestedId || parentPanelId
      }
    }));
  });
};
var SidePanelNestedPanels$1 = SidePanelNestedPanels;

export { SidePanelNestedPanels$1 as default };
